/**
 * AdminPartnerVerificationsPage — /admin/partner-verifications
 *
 * Phase H Step 1 admin queue. Lists every `partner_verifications` row
 * with the four admin actions:
 *
 *   pick-up      — identity_completed → admin_in_review
 *                  (also allowed from needs_info → admin_in_review
 *                   when the partner responds and admin re-picks-up)
 *   request-info — admin_in_review → needs_info (requires reason)
 *   approve      — admin_in_review → approved (terminal, flips
 *                  users.partner_is_active=True)
 *   reject       — admin_in_review → rejected (terminal, requires reason)
 *
 * Copy discipline (Family H): only the two permitted claims —
 * "Identity-verified via Stripe" and "Manually reviewed by our team".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, ShieldCheck } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = [
  "verification_pending",
  "identity_in_progress",
  "identity_completed",
  "admin_in_review",
  "needs_info",
  "approved",
  "rejected",
];

function labelFor(s) {
  return s.replace(/_/g, " ");
}

/* ------------------------------------------------------------------ */
export default function AdminPartnerVerificationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pendingAction, setPendingAction] = useState(null); // { row, kind }
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/admin/partner-verifications", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => {
    setPendingAction(null);
    setReason("");
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const { row, kind } = pendingAction;
    if ((kind === "request-info" || kind === "reject") && !reason.trim()) {
      toast.error("A reason is required for this action.");
      return;
    }
    setSubmitting(true);
    try {
      const body =
        kind === "request-info" || kind === "reject"
          ? { reason: reason.trim() }
          : {};
      await api.post(
        `/admin/partner-verifications/${row.id}/${kind}`,
        body,
      );
      toast.success(`Action complete: ${kind}`);
      closeModal();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length };
    STATUSES.forEach((s) => (c[s] = rows.filter((r) => r.status === s).length));
    return c;
  }, [rows]);

  return (
    <div
      style={{ padding: "24px" }}
      data-testid="admin-partner-verifications-page"
    >
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={22} color="#F89623" />
          <h1
            style={{ fontSize: 24, fontWeight: 800, color: "#374751", margin: 0 }}
            data-testid="admin-partner-verifications-heading"
          >
            Partner Verifications
          </h1>
        </div>
        <p style={{ color: "#4b5563", marginTop: 6 }}>
          Identity-verified via Stripe · Manually reviewed by our team.
        </p>
      </header>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          alignItems: "center",
        }}
      >
        <div style={{ minWidth: 220 }}>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-partner-verifications-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses ({counts.total})</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {labelFor(s)} ({counts[s]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-partner-verifications-refresh"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <RefreshCw size={16} style={{ marginRight: 6 }} /> Refresh
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div data-testid="admin-partner-verifications-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="admin-partner-verifications-empty"
          style={{
            padding: 24,
            background: "#f9fafb",
            borderRadius: 12,
            color: "#6b7280",
          }}
        >
          No verification rows in this bucket.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 14,
            }}
            data-testid="admin-partner-verifications-table"
          >
            <thead>
              <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                <th style={cellHead}>Partner user id</th>
                <th style={cellHead}>Status</th>
                <th style={cellHead}>Provider</th>
                <th style={cellHead}>Updated</th>
                <th style={cellHead}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  style={{ borderTop: "1px solid #e5e7eb" }}
                  data-testid={`admin-partner-verifications-row-${r.id}`}
                >
                  <td style={cell}>
                    <code style={{ fontSize: 12 }}>{r.partner_user_id}</code>
                  </td>
                  <td style={cell}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "#f3f4f6",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                      data-testid={`admin-partner-verifications-row-status-${r.id}`}
                    >
                      {labelFor(r.status)}
                    </span>
                  </td>
                  <td style={cell}>{r.identity_provider}</td>
                  <td style={cell}>{r.updated_at?.slice(0, 16)}</td>
                  <td style={cell}>
                    <RowActions
                      row={r}
                      onPickUp={() =>
                        setPendingAction({ row: r, kind: "pick-up" })
                      }
                      onRequestInfo={() =>
                        setPendingAction({ row: r, kind: "request-info" })
                      }
                      onApprove={() =>
                        setPendingAction({ row: r, kind: "approve" })
                      }
                      onReject={() =>
                        setPendingAction({ row: r, kind: "reject" })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={!!pendingAction}
        onOpenChange={(o) => !o && closeModal()}
      >
        <DialogContent data-testid="admin-partner-verifications-action-modal">
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.kind === "pick-up" && "Pick up for review"}
              {pendingAction?.kind === "request-info" && "Request more info"}
              {pendingAction?.kind === "approve" && "Approve partner"}
              {pendingAction?.kind === "reject" && "Reject application"}
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.kind === "approve" &&
                "Marks the partner as a Verified PooPartner and flips them active. Cannot be undone."}
              {pendingAction?.kind === "reject" &&
                "Marks the application as not approved. Cannot be undone."}
              {pendingAction?.kind === "request-info" &&
                "Sends the partner an email with your note and pauses the review."}
              {pendingAction?.kind === "pick-up" &&
                "Assigns this row to you for manual review."}
            </DialogDescription>
          </DialogHeader>
          {(pendingAction?.kind === "request-info" ||
            pendingAction?.kind === "reject") && (
            <div style={{ marginTop: 8 }}>
              <Label htmlFor="pv-reason">Reason (required)</Label>
              <Textarea
                id="pv-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain what the partner should know…"
                rows={4}
                data-testid="admin-partner-verifications-reason-input"
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeModal}
              disabled={submitting}
              data-testid="admin-partner-verifications-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={runAction}
              disabled={submitting}
              data-testid="admin-partner-verifications-confirm-btn"
            >
              {submitting ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function RowActions({ row, onPickUp, onRequestInfo, onApprove, onReject }) {
  const s = row.status;
  const canPickUp = s === "identity_completed" || s === "needs_info";
  const canReview = s === "admin_in_review";
  if (!canPickUp && !canReview) {
    return (
      <span style={{ color: "#6b7280", fontSize: 12 }}>
        No actions
      </span>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {canPickUp && (
        <Button
          size="sm"
          onClick={onPickUp}
          data-testid={`admin-partner-verifications-pick-up-${row.id}`}
        >
          Pick up
        </Button>
      )}
      {canReview && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={onRequestInfo}
            data-testid={`admin-partner-verifications-request-info-${row.id}`}
          >
            Request info
          </Button>
          <Button
            size="sm"
            onClick={onApprove}
            data-testid={`admin-partner-verifications-approve-${row.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onReject}
            data-testid={`admin-partner-verifications-reject-${row.id}`}
          >
            Reject
          </Button>
        </>
      )}
    </div>
  );
}

const cellHead = {
  padding: "10px 14px",
  fontWeight: 700,
  color: "#374751",
  fontSize: 13,
};
const cell = { padding: "10px 14px", color: "#374751" };
